<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  active: {
    type: Boolean,
    default: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  automationId: {
    type: [String, Number],
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:name', 'update:active', 'save', 'cancel']);

const { t } = useI18n();
const { showAlert } = useAlert();

// Export
const isExporting = ref(false);

const handleExport = async () => {
  if (props.readonly || !props.automationId || isExporting.value) return;
  isExporting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.exportAutomation(
      props.automationId
    );
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (props.name || 'automation')
      .replace(/[^a-z0-9_-]/gi, '_')
      .toLowerCase();
    link.href = url;
    link.download = `${safeName}_automation.json`;
    link.click();
    URL.revokeObjectURL(url);
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
};
const localName = ref(props.name);
const localActive = ref(props.active);

// Inline title editing
const isEditingTitle = ref(false);
const titleInputRef = ref(null);
const titleBeforeEdit = ref('');

const startEditingTitle = () => {
  if (props.readonly) return;
  titleBeforeEdit.value = localName.value;
  isEditingTitle.value = true;
  nextTick(() => {
    titleInputRef.value?.focus();
    titleInputRef.value?.select();
  });
};

const commitTitleEdit = () => {
  if (!isEditingTitle.value) return;
  isEditingTitle.value = false;
  const trimmed = localName.value.trim();
  localName.value = trimmed;
  emit('update:name', trimmed);
};

const cancelTitleEdit = () => {
  localName.value = titleBeforeEdit.value;
  isEditingTitle.value = false;
};

// Save-time name modal
const showSaveModal = ref(false);
const modalNameInput = ref('');
const modalInputRef = ref(null);

watch(
  () => props.name,
  newValue => {
    localName.value = newValue;
  }
);

watch(
  () => props.active,
  newValue => {
    localActive.value = newValue;
  }
);

const handleActiveChange = () => {
  if (props.readonly) return;
  emit('update:active', localActive.value);
};

const handleSaveClick = () => {
  if (props.readonly) return;
  if (localName.value.trim()) {
    emit('save');
  } else {
    modalNameInput.value = '';
    showSaveModal.value = true;
    nextTick(() => {
      modalInputRef.value?.focus();
    });
  }
};

const handleModalSave = () => {
  if (props.readonly) return;
  const trimmed = modalNameInput.value.trim();
  if (!trimmed) return;
  localName.value = trimmed;
  emit('update:name', trimmed);
  showSaveModal.value = false;
  emit('save');
};

const handleModalCancel = () => {
  showSaveModal.value = false;
};
</script>

<template>
  <header
    data-testid="automation-header"
    class="flex min-w-0 flex-col gap-3 border-b border-n-weak bg-n-solid-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
  >
    <div class="flex min-w-0 items-center gap-2 sm:gap-3">
      <button
        type="button"
        class="flex size-11 shrink-0 items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        :aria-label="t('PIPELINE.FLOW_BUILDER.BACK_TO_AUTOMATIONS')"
        @click="$emit('cancel')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <input
        v-if="!readonly && isEditingTitle"
        ref="titleInputRef"
        v-model="localName"
        type="text"
        name="automation-title"
        autocomplete="off"
        class="min-h-11 min-w-0 flex-1 border-b border-n-brand bg-transparent px-2 text-heading-1 text-n-slate-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand sm:min-w-48 sm:max-w-xs"
        :aria-label="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.TITLE_INPUT_LABEL')"
        :placeholder="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')"
        @blur="commitTitleEdit"
        @keydown.enter.prevent="commitTitleEdit"
        @keydown.esc.prevent.stop="cancelTitleEdit"
      />
      <button
        v-else-if="!readonly"
        type="button"
        data-testid="automation-title-trigger"
        class="flex min-h-11 min-w-0 flex-1 items-center rounded px-2 text-left text-heading-1 text-n-slate-12 transition-colors hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand sm:max-w-md"
        :title="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.EDIT_TITLE_HINT')"
        :aria-label="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.EDIT_TITLE_HINT')"
        @click="startEditingTitle"
      >
        <span class="truncate">
          {{
            localName.trim() ||
            t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')
          }}
        </span>
      </button>
      <h1
        v-else
        data-testid="automation-readonly-title"
        class="min-w-0 flex-1 truncate px-2 text-heading-1 text-n-slate-12 sm:max-w-md"
      >
        {{
          localName.trim() ||
          t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')
        }}
      </h1>
    </div>

    <div
      data-testid="automation-header-actions"
      class="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end"
    >
      <!-- Export button (only for saved automations) -->
      <button
        v-if="automationId && !readonly"
        type="button"
        class="flex min-h-11 items-center gap-1.5 rounded-md bg-n-alpha-1 px-3 py-1.5 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="isExporting"
        @click="handleExport"
      >
        <span class="i-lucide-download size-4" aria-hidden="true" />
        {{
          isExporting
            ? t('PIPELINE.FLOW_BUILDER.EXPORTING')
            : t('PIPELINE.FLOW_BUILDER.EXPORT')
        }}
      </button>

      <div v-if="!readonly" class="flex items-center">
        <label
          for="automation-active-toggle"
          class="flex min-h-11 items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
        >
          {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.ACTIVE') }}
          <span class="relative">
            <input
              id="automation-active-toggle"
              v-model="localActive"
              type="checkbox"
              class="sr-only peer"
              :aria-label="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.ACTIVE')"
              @change="handleActiveChange"
            />
            <span
              class="block h-5 w-10 rounded-full bg-n-alpha-2 transition-colors peer-checked:bg-n-brand peer-focus-visible:ring-2 peer-focus-visible:ring-n-brand peer-focus-visible:ring-offset-2"
              aria-hidden="true"
            />
            <span
              class="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-[18px]"
              aria-hidden="true"
            />
          </span>
        </label>
      </div>

      <button
        v-if="!readonly"
        type="button"
        class="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-md bg-n-brand px-3 py-1.5 text-sm font-medium text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/40 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
        :disabled="isSaving"
        @click="handleSaveClick"
      >
        <span
          v-if="isSaving"
          class="i-lucide-loader-2 size-4 animate-spin"
          aria-hidden="true"
        />
        <span v-else class="i-lucide-save size-4" aria-hidden="true" />
        {{
          isSaving
            ? t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVING')
            : t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVE')
        }}
      </button>

      <button
        type="button"
        class="flex min-h-11 flex-1 items-center justify-center gap-1 rounded-md bg-n-alpha-1 px-3 py-1.5 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand sm:flex-none"
        @click="$emit('cancel')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.CANCEL') }}
      </button>
    </div>
  </header>

  <!-- Name prompt modal — shown when saving without a name -->
  <Modal v-model:show="showSaveModal" :on-close="handleModalCancel">
    <div class="w-full px-5 py-6 sm:px-8 sm:py-7">
      <!-- Icon + heading -->
      <div class="flex items-start gap-4 mb-6">
        <div
          class="flex-shrink-0 w-10 h-10 rounded-xl bg-n-brand/10 flex items-center justify-center"
        >
          <span class="i-lucide-zap size-5 text-n-brand" />
        </div>
        <div>
          <h3 class="text-base font-semibold text-n-slate-12 leading-snug">
            {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-0.5">
            {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Input -->
      <label
        for="automation-save-name"
        class="block text-xs font-medium text-n-slate-11 mb-1.5 uppercase tracking-wide"
      >
        {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.NAME_LABEL') }}
      </label>
      <input
        id="automation-save-name"
        ref="modalInputRef"
        v-model="modalNameInput"
        type="text"
        name="automation-save-name"
        autocomplete="off"
        class="min-h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 transition-shadow placeholder:text-n-slate-9 focus:border-n-brand/50 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
        :placeholder="t('PIPELINE.AUTOMATIONS.SAVE_MODAL.NAME_PLACEHOLDER')"
        @keydown.enter="handleModalSave"
      />

      <!-- Actions -->
      <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row">
        <button
          type="button"
          class="min-h-11 flex-1 rounded-lg bg-n-alpha-1 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="handleModalCancel"
        >
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.CANCEL') }}
        </button>
        <button
          type="button"
          class="min-h-11 flex-1 rounded-lg bg-n-brand py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="!modalNameInput.trim()"
          @click="handleModalSave"
        >
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
